import { Grid } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import translation from './activity.uha.i18n';
import { useIntl } from 'react-intl';
import {
  HttpError,
  PeriodeDto,
  PraksisaktivitetInfoDto,
  PraksisklasseMedAktiviteterDto,
  UndervisningsenhetToPraksisklasseMedAktiviteterDto,
} from '../api/generated/studiested-generated-api';
import DatePicker from '../components/datepicker';
import * as css from './activity.uha.module.scss';
import { TextInput } from '../components/input';
import { ConfirmationDialog } from '../components/dialog';
import Dropdown, { DropdownOption } from '../components/input/dropdown';
import Checkbox from '../components/input/checkbox';
import PanelHeader, {
  HeaderLevel,
} from '../components/panelheader/panelHeader';
import { parseIsoTzLess, toStartOfDay } from '../date.utils';
import Button from '../components/button';
import { useLoader } from '../api/useLoader';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { NotificationFunctionContext } from '../components/notifications/notificationProvider';
import { UhaUserContext } from '../user/UhaUserStore';
import { BrukerrollerDto } from '../api/generated/praksissted-generated-api';
import { useActivityContextUHA } from './activitycontexts';
import { Language, LanguageContext, toLocaleTag } from '../i18n/LanguageStore';
import dayjs from 'dayjs';
import { useFormatMessageFor } from '../i18n/translation';

const ActivityInfo = () => {
  const { praksisaktivitet } = useActivityContextUHA();
  const [copyModalOpen, setCopyModalOpen] = useState(false);

  const { uhaUser: bruker } = useContext(UhaUserContext);
  const canWrite =
    bruker && bruker.hoeyesteRettighet !== BrukerrollerDto.Leserettighet;

  const labels = useFormatMessageFor(translation);

  const { kapasitet, startDato, sluttDato, behov, studenter, klasse } =
    praksisaktivitet;

  const klassenavn =
    praksisaktivitet.klasse.presentertNavn || praksisaktivitet.klasse.bokstav;

  const onCopyClick = (event: React.SyntheticEvent) => {
    const { nativeEvent } = event;

    if (nativeEvent.type === 'KeyboardEvent') {
      if ((nativeEvent as KeyboardEvent).key === 'Enter') {
        setCopyModalOpen(true);
      }
    } else {
      setCopyModalOpen(true);
    }
  };

  return (
    <>
      <div className={css.verticalMargin}>
        <Grid container justifyContent={'space-between'}>
          <Grid item lg={4}>
            <Grid container spacing={1}>
              <Grid xs={5} item>
                <DatePicker
                  value={startDato}
                  label={labels.dateFrom}
                  onChange={() => {}}
                  disabled
                  dataCy="activity-info-start-date"
                />
              </Grid>
              <Grid xs={5} item>
                <DatePicker
                  value={sluttDato}
                  label={labels.dateTo}
                  onChange={() => {}}
                  disabled
                  dataCy="activity-info-end-date"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid md={12} lg={8} item>
            <Grid container justifyContent={'space-between'}>
              <Grid item xs={6} lg={2}>
                <TextInput
                  htmlId="behov-fake"
                  inputClassName={css.smallInput}
                  value={behov.toString(10)}
                  label={labels.estimatedPlacesNeed}
                  onChange={() => {}}
                  disabled
                  hideBorder
                  dataCy="activity-info-estimert-behov"
                />
              </Grid>
              <Grid item xs={6} lg={2}>
                <TextInput
                  htmlId="kapasitet-fake"
                  inputClassName={css.smallInput}
                  value={kapasitet.toString(10)}
                  label={labels.capacity}
                  onChange={() => {}}
                  disabled
                  hideBorder
                  dataCy="activity-info-kapasitet"
                />
              </Grid>
              <Grid item xs={6} lg={2}>
                <span title={labels.realNeedTooltip}>
                  <TextInput
                    htmlId="studenter-fake"
                    inputClassName={css.smallInput}
                    value={studenter.toString(10)}
                    label={labels.realNeed}
                    onChange={() => {}}
                    disabled
                    hideBorder
                    dataCy="activity-info-reelt-behov"
                  />
                </span>
              </Grid>
              <Grid item xs={4} lg={4}>
                <span title={`${labels.aktivitetsklasse} - ${klassenavn}`}>
                  <TextInput
                    htmlId="klasse-input"
                    inputClassName={
                      klassenavn !== klasse.bokstav
                        ? css.klasseAndPeriodeInput
                        : css.smallInput
                    }
                    value={klassenavn}
                    label={labels.aktivitetsklasse}
                    onChange={() => {}}
                    disabled
                    hideBorder
                    dataCy="activity-info-klassenavn"
                  />
                </span>
              </Grid>
              {canWrite && (
                <Grid item xs={2}>
                  <div className={css.copyIconBtn}>
                    <Button.Add
                      title={labels.copyActivityModalTitle}
                      onClick={onCopyClick}
                      data-cy={'copy-activity-button'}
                    >
                      {labels.copyActivityModalTitleShort}
                    </Button.Add>
                  </div>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </div>
      {copyModalOpen && (
        <CopyActivityModalWrapper close={() => setCopyModalOpen(false)} />
      )}
    </>
  );
};

interface CopyActivityModalWrapperProps {
  close: () => void;
}

interface CopyActivityModalProps extends CopyActivityModalWrapperProps {
  undervisningsenhetToPraksisklasseMedAktiviteterCollection: UndervisningsenhetToPraksisklasseMedAktiviteterDto[];
}

type CheckboxesForCopy = {
  aktivitetskrav: boolean;
  fritekst: boolean;
  bestillinger: boolean;
  roller: boolean;
  fordeling: boolean;
};

const initPeriodeDto: PeriodeDto = {
  startDato: '',
  sluttDato: '',
};

const praksisklasseMedAktiviteterToDropdownOption = (
  klasseMedAktiviteter: PraksisklasseMedAktiviteterDto,
): DropdownOption => {
  const { praksisklasse } = klasseMedAktiviteter;

  return {
    ...klasseMedAktiviteter,
    id: praksisklasse.id,
    navn: praksisklasse.presentertNavn || praksisklasse.bokstav,
  };
};

const toYearMonthLocaleString = (date: Date, userLanguage: Language) =>
  date.toLocaleString(toLocaleTag(userLanguage), {
    year: 'numeric',
    month: 'short',
  });

const undervisningsenhetPeriodeToDropdownOption = (
  undervisningsenhetToPraksisklasseMedAktiviteterDto: UndervisningsenhetToPraksisklasseMedAktiviteterDto,
  userLanguage: Language,
): DropdownOption => {
  const { undervisningsenhetPeriode } =
    undervisningsenhetToPraksisklasseMedAktiviteterDto;

  return {
    ...undervisningsenhetToPraksisklasseMedAktiviteterDto,
    id: undervisningsenhetPeriode.undervisningsenhetId,
    navn: `${toYearMonthLocaleString(
      new Date(undervisningsenhetPeriode.start),
      userLanguage,
    )} - ${toYearMonthLocaleString(
      new Date(undervisningsenhetPeriode.slutt),
      userLanguage,
    )}`,
  };
};

const validateCheckboxStates = (
  checkboxes: CheckboxesForCopy,
  changedKlasse: boolean,
): boolean => {
  const { bestillinger, fordeling, roller } = checkboxes;

  return (
    (!changedKlasse || (changedKlasse && !fordeling)) &&
    (bestillinger || (!fordeling && !roller))
  );
};

const overlapInPeriod = (
  aktivitet: PraksisaktivitetInfoDto,
  periode: PeriodeDto,
) => {
  const aktivitetStartDato = new Date(aktivitet.startDato);
  const aktivitetSluttDato = new Date(aktivitet.sluttDato);
  const periodeStartDato = new Date(periode.startDato);
  const periodeSluttDato = new Date(periode.sluttDato);

  return (
    (periodeStartDato >= aktivitetStartDato &&
      periodeStartDato <= aktivitetSluttDato) ||
    (periodeSluttDato <= aktivitetSluttDato &&
      periodeSluttDato >= aktivitetStartDato)
  );
};

const CopyActivityModalWrapper: React.FC<
  React.PropsWithChildren<CopyActivityModalWrapperProps>
> = ({ ...props }) => {
  const { praksiskalenderApi } = useContext(StudiestedApiContext);
  const { praksisaktivitet } = useActivityContextUHA();

  const { data } = useLoader(
    () =>
      praksiskalenderApi.undervisningsenheterKlasserPraksisaktiviteterGet({
        queryParams: {
          undervisningsenhetId:
            praksisaktivitet.klasse.undervisningsenhetPeriode
              .undervisningsenhetId,
        },
      }),
    [praksisaktivitet],
  );

  return data ? (
    <CopyActivityModal
      undervisningsenhetToPraksisklasseMedAktiviteterCollection={data}
      {...props}
    />
  ) : (
    <></>
  );
};

// Exported for tests
export const CopyActivityModal: React.FC<
  React.PropsWithChildren<CopyActivityModalProps>
> = ({ undervisningsenhetToPraksisklasseMedAktiviteterCollection, close }) => {
  const { praksiskalenderApi } = useContext(StudiestedApiContext);
  const { praksisaktivitet } = useActivityContextUHA();
  const { addSuccessNotification, addErrorNotification } = useContext(
    NotificationFunctionContext,
  );
  const { selectedLanguage } = useContext(LanguageContext);

  const [undenhetKlasseAktiviteter, setUndenhetKlasseAktiviteter] = useState(
    undervisningsenhetToPraksisklasseMedAktiviteterCollection[0],
  );

  const [periode, setPeriode] = useState(initPeriodeDto);

  const [praksisklasseMedAktiviteter, setPraksisklasseMedAktiviteter] =
    useState(
      undenhetKlasseAktiviteter.praksisklasseMedAktiviteter.find(
        (klasse) => praksisaktivitet.klasse.id === klasse.praksisklasse.id,
      ) || undenhetKlasseAktiviteter.praksisklasseMedAktiviteter[0],
    );

  const [checkboxes, setCheckboxes] = useState<CheckboxesForCopy>({
    aktivitetskrav: true,
    fritekst: true,
    bestillinger: true,
    roller: true,
    fordeling: true,
  });

  const { formatMessage } = useIntl();

  const labels = useMemo(
    () => ({
      copyActivityModalTitle: formatMessage({
        id: translation.copyActivityModalTitle.id,
      }),
      copyActivityModalSubtitle: formatMessage({
        id: translation.copyActivityModalSubtitle.id,
      }),
      copyActivityModalSubmit: formatMessage({
        id: translation.copyActivityModalSubmit.id,
      }),
      cancel: formatMessage({ id: translation.cancel.id }),
      dateFrom: formatMessage({ id: translation.dateFrom.id }),
      dateTo: formatMessage({ id: translation.dateTo.id }),
      class: formatMessage({ id: translation.aktivitetsklasse.id }),
      undervisningsperiode: formatMessage({
        id: translation.copyActivityModalUndervisningsperiode.id,
      }),
      copyActivityModalAktivitetskrav: formatMessage({
        id: translation.copyActivityModalAktivitetskrav.id,
      }),
      copyActivityModalFritekst: formatMessage({
        id: translation.copyActivityModalFritekst.id,
      }),
      copyActivityModalBestillingerBesvarelser: formatMessage({
        id: translation.copyActivityModalBestillingerBesvarelser.id,
      }),
      roller: formatMessage({
        id: translation.roller.id,
      }),
      copyActivityModalFordeling: formatMessage({
        id: translation.copyActivityModalFordeling.id,
      }),
      copyActivityModalPeriodeKlasseHeader: formatMessage({
        id: translation.copyActivityModalPeriodeKlasseHeader.id,
      }),
      copyActivityModalPeriodeKlasseHeaderSubtitle: formatMessage({
        id: translation.copyActivityModalPeriodeKlasseHeaderSubtitle.id,
      }),
      copyActivityModalPartsHeader: formatMessage({
        id: translation.copyActivityModalPartsHeader.id,
      }),
      copyActivityModalPartsHeaderSubtitle: formatMessage({
        id: translation.copyActivityModalPartsHeaderSubtitle.id,
      }),
      copyActivityModalOverlappingPeriod: formatMessage({
        id: translation.copyActivityModalOverlappingPeriod.id,
      }),
      copyActivityModalSuccessMsg: formatMessage({
        id: translation.copyActivityModalSuccessMsg.id,
      }),
      copyActivityModalChangedKlasse: formatMessage({
        id: translation.copyActivityModalChangedKlasse.id,
      }),
    }),
    [formatMessage],
  );

  const today = useMemo(() => toStartOfDay(new Date(Date.now())), []);

  const undenhetStart = useMemo(
    () => new Date(undenhetKlasseAktiviteter.undervisningsenhetPeriode.start),
    [undenhetKlasseAktiviteter.undervisningsenhetPeriode.start],
  );

  const undenhetSlutt = useMemo(
    () => new Date(undenhetKlasseAktiviteter.undervisningsenhetPeriode.slutt),
    [undenhetKlasseAktiviteter.undervisningsenhetPeriode.slutt],
  );

  const fromDateMin = useMemo(
    () => (undenhetStart > today ? undenhetStart : today),
    [undenhetStart, today],
  );

  const fromDateMax = useMemo(
    () =>
      periode.sluttDato ? parseIsoTzLess(periode.sluttDato) : undenhetSlutt,
    [undenhetSlutt, periode.sluttDato],
  );

  const toDateMin = useMemo(() => {
    if (periode.startDato) {
      return parseIsoTzLess(periode.startDato);
    }

    return undenhetStart > today ? undenhetStart : today;
  }, [periode.startDato, undenhetStart, today]);

  const anyOverlappingPeriod = useMemo(
    () =>
      praksisklasseMedAktiviteter.aktiviteter.some((aktivitet) =>
        overlapInPeriod(aktivitet, periode),
      ),
    [periode, praksisklasseMedAktiviteter],
  );

  const correctlyOrderedToDateAndFromDate = useMemo(() => {
    const { startDato, sluttDato } = periode;

    const periodeStartDato = toStartOfDay(new Date(startDato));
    const periodeSluttDato = toStartOfDay(new Date(sluttDato));

    return (
      periodeStartDato >= fromDateMin &&
      periodeStartDato <= fromDateMax &&
      periodeSluttDato >= toDateMin &&
      periodeSluttDato <= undenhetSlutt
    );
  }, [periode, fromDateMin, toDateMin, fromDateMax, undenhetSlutt]);

  const changedKlasse = useMemo(
    () =>
      praksisaktivitet.klasse.id !==
      praksisklasseMedAktiviteter.praksisklasse.id,
    [praksisaktivitet.klasse, praksisklasseMedAktiviteter.praksisklasse],
  );

  const validDate = useMemo(
    () =>
      periode.startDato &&
      periode.sluttDato &&
      correctlyOrderedToDateAndFromDate &&
      !anyOverlappingPeriod,
    [anyOverlappingPeriod, periode, correctlyOrderedToDateAndFromDate],
  );

  const validCheckboxes = useMemo(
    () => validateCheckboxStates(checkboxes, changedKlasse),
    [checkboxes, changedKlasse],
  );

  const validInput = useMemo(
    () => validDate && validCheckboxes,
    [validDate, validCheckboxes],
  );

  useEffect(() => {
    if (changedKlasse) {
      setCheckboxes((prevState) => ({ ...prevState, fordeling: false }));
    }
  }, [changedKlasse]);

  const onClose = (submit: boolean) => {
    if (submit) {
      const { id: praksisklasseId } = praksisklasseMedAktiviteter.praksisklasse;
      const {
        aktivitetskrav: kopierAktivitetskrav,
        bestillinger: kopierBestillinger,
        roller: kopierRoller,
        fordeling: kopierFordeling,
        fritekst: kopierFritekst,
      } = checkboxes;

      praksiskalenderApi
        .praksisaktivitetIdKopiPost({
          pathParams: {
            id: praksisaktivitet.id,
          },
          praksisaktivitetKopiPostDto: {
            praksisklasseId,
            periode,
            egenskaper: {
              kopierAktivitetskrav,
              kopierBestillinger,
              kopierRoller,
              kopierFordeling,
              kopierFritekst,
            },
          },
        })
        .then(() => {
          addSuccessNotification(labels.copyActivityModalSuccessMsg);
          close();
        })
        .catch((err: HttpError) => addErrorNotification(err.body));
    } else {
      close();
    }
  };

  return (
    <ConfirmationDialog
      title={labels.copyActivityModalTitle}
      subtitle={labels.copyActivityModalSubtitle}
      cancelLabel={labels.cancel}
      submitLabel={labels.copyActivityModalSubmit}
      submitDisabled={!validInput}
      onClose={onClose}
      extraClasses={[css.copyModal]}
      isOpen
    >
      <PanelHeader
        title={labels.copyActivityModalPeriodeKlasseHeader}
        subtitle={labels.copyActivityModalPeriodeKlasseHeaderSubtitle}
        className={css.header}
        headerLevel={HeaderLevel.h3}
        collapsible
      >
        <div className={css.modalSelectPeriode}>
          <Dropdown
            id={'copy-activity-undervisningsperiode'}
            label={labels.undervisningsperiode}
            onChange={(
              _,
              value: UndervisningsenhetToPraksisklasseMedAktiviteterDto,
            ) => {
              setPeriode(initPeriodeDto);
              setUndenhetKlasseAktiviteter(value);

              const possibleKlasser = value.praksisklasseMedAktiviteter;
              const defaultKlasse = possibleKlasser[0];

              setPraksisklasseMedAktiviteter(
                (prevState) =>
                  possibleKlasser.find(
                    (klasse: PraksisklasseMedAktiviteterDto) =>
                      prevState.praksisklasse.bokstav ===
                      klasse.praksisklasse.bokstav,
                  ) ?? defaultKlasse,
              );
            }}
            selectedOption={undervisningsenhetPeriodeToDropdownOption(
              undenhetKlasseAktiviteter,
              selectedLanguage,
            )}
            options={undervisningsenhetToPraksisklasseMedAktiviteterCollection.map(
              (undervisningsenhetToPraksisklasseMedAktiviteter) =>
                undervisningsenhetPeriodeToDropdownOption(
                  undervisningsenhetToPraksisklasseMedAktiviteter,
                  selectedLanguage,
                ),
            )}
            className={css.klasseAndPeriodeInput}
            dataCy={'undervisningsperiode-dropdown-input'}
            required
          />
          <DatePicker
            value={periode.startDato}
            label={labels.dateFrom}
            onChange={(date) => setPeriode({ ...periode, startDato: date })}
            className={css.dateInput}
            constraints={{
              minDate: dayjs(fromDateMin),
              maxDate: dayjs(fromDateMax),
            }}
            dataCy={'date-picker-start'}
            required
          />
          <DatePicker
            value={periode.sluttDato}
            label={labels.dateTo}
            onChange={(date) => setPeriode({ ...periode, sluttDato: date })}
            className={css.dateInput}
            constraints={{
              minDate: dayjs(toDateMin),
              maxDate: dayjs(undenhetSlutt),
            }}
            dataCy={'date-picker-slutt'}
            required
          />
        </div>
        <Dropdown
          id={'copy-activity-class'}
          label={labels.class}
          onChange={(_, klasse) => setPraksisklasseMedAktiviteter(klasse)}
          selectedOption={praksisklasseMedAktiviteterToDropdownOption(
            praksisklasseMedAktiviteter,
          )}
          options={undenhetKlasseAktiviteter.praksisklasseMedAktiviteter.map(
            praksisklasseMedAktiviteterToDropdownOption,
          )}
          className={css.klasseAndPeriodeInput}
          dataCy={'praksisklasse-dropdown-input'}
        />
        <p
          className={css.overlapWarning}
          role={anyOverlappingPeriod || changedKlasse ? 'alert' : undefined}
        >
          {anyOverlappingPeriod && labels.copyActivityModalOverlappingPeriod}
          {changedKlasse &&
            `${anyOverlappingPeriod ? ' ' : ''}${
              labels.copyActivityModalChangedKlasse
            }`}
        </p>
      </PanelHeader>
      <PanelHeader
        title={labels.copyActivityModalPartsHeader}
        subtitle={labels.copyActivityModalPartsHeaderSubtitle}
        className={css.header}
        headerLevel={HeaderLevel.h3}
        collapsible
      >
        <div className={css.modalCheckboxes}>
          <Checkbox
            id={'aktivitetskrav'}
            label={labels.copyActivityModalAktivitetskrav}
            checked={checkboxes.aktivitetskrav}
            setChecked={(checked) =>
              setCheckboxes({ ...checkboxes, aktivitetskrav: checked })
            }
          />
          <Checkbox
            id={'fritekst'}
            label={labels.copyActivityModalFritekst}
            checked={checkboxes.fritekst}
            setChecked={(checked) =>
              setCheckboxes({ ...checkboxes, fritekst: checked })
            }
          />
          <Checkbox
            id={'bestillinger'}
            label={labels.copyActivityModalBestillingerBesvarelser}
            checked={checkboxes.bestillinger}
            setChecked={(checked) =>
              checked
                ? setCheckboxes({ ...checkboxes, bestillinger: true })
                : setCheckboxes({
                    ...checkboxes,
                    bestillinger: false,
                    fordeling: false,
                    roller: false,
                  })
            }
          />
          <Checkbox
            id={'roller'}
            label={labels.roller}
            checked={checkboxes.roller}
            setChecked={(checked) =>
              setCheckboxes({ ...checkboxes, roller: checked })
            }
            disabled={!checkboxes.bestillinger}
          />
          <Checkbox
            id={'fordeling'}
            label={labels.copyActivityModalFordeling}
            checked={checkboxes.fordeling}
            setChecked={(checked) =>
              setCheckboxes({ ...checkboxes, fordeling: checked })
            }
            disabled={!checkboxes.bestillinger || changedKlasse}
          />
        </div>
      </PanelHeader>
    </ConfirmationDialog>
  );
};

export default ActivityInfo;
